import { useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

/**
 * CommonImagePicker
 *
 * @param {string|null} imageURL  - Current image URL (null / empty for new record)
 * @param {function}    onChange  - Called with the selected File when user picks a new image
 * @param {function}    onReset   - Called when user resets to the original image
 */
export default function CommonImagePicker({ 
    imageURL, onChange, onReset, 
    uploadCaption = "Upload Image",
    suggestedResolution = '1920 × 1080' }) {
    const inputRef = useRef(null);
    const [previewURL, setPreviewURL] = useState(null);

    const displayURL = previewURL ?? (imageURL || null);
    const hasOriginal = Boolean(imageURL);
    const hasChanged = Boolean(previewURL);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const objectURL = URL.createObjectURL(file);
        setPreviewURL(objectURL);
        onChange?.(file);
        // reset input so the same file can be re-selected if needed
        e.target.value = '';
    };

    const handleReset = () => {
        if (previewURL) {
            URL.revokeObjectURL(previewURL);
            setPreviewURL(null);
        }
        onReset?.();
    };

    return (
        <Stack spacing={1.5}>
            {/* Image preview */}
            
            {displayURL && (
                <Box
                    component="img"
                    src={displayURL}
                    alt="preview"
                    sx={{
                        width: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.100',
                    }}
                />
            )}

            {/* Hint text */}
            <Typography variant="caption" color="text.secondary">
                Accepted formats: PNG, JPEG &nbsp;·&nbsp; 
                Suggested resolution: {suggestedResolution}
            </Typography>

            {/* Action buttons */}
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => inputRef.current?.click()}
                >
                    {uploadCaption}
                </Button>

                {(hasOriginal || hasChanged) && (
                    <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        startIcon={<RestartAltIcon />}
                        onClick={handleReset}
                        disabled={!hasChanged}
                    >
                        Reset
                    </Button>
                )}
            </Stack>

            {/* Hidden file input */}
            <input
                ref={inputRef}
                type="file"
                accept="image/png, image/jpeg"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </Stack>
    );
}
